import React, { forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import styles from './hot-filter-search-input.module.scss';

type HotFilterSearchInputProps = {
  placeholder: string;
  value: string | string[];
  onChange: (arg: string) => void;
  onReset?: () => void;
  hideIcon?: boolean;
  identifier?: string;
} & React.HTMLProps<HTMLInputElement>;

const HotFilterSearchInput = forwardRef<{ reset: () => void }, HotFilterSearchInputProps>((props, ref) => {
  const { t } = useTranslation();
  const { placeholder, value, hideIcon = false, onChange, onReset, identifier } = props;

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const handleReset = () => {
    onReset();
  };

  const handleEscPress = (e) => {
    if (e.code === 'Escape') {
      handleReset();
    }
  };

  useImperativeHandle(ref, () => ({ reset: handleReset }), []);

  const id = identifier || 'filter-search';

  return (
    <div className={styles.hotFilterSearchInputWrapper}>
      {!hideIcon && (
        <Icon
          name="search-default"
          className={styles.hotFilterSearchIcon}
          ariaHidden
        />
      )}
      <label
        htmlFor={id}
        className="visuallyhidden"
      >
        {placeholder}
      </label>
      <input
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className={clsx(styles.hotFilterSearchInput, { [styles.hotFilterSearchInputWithIcon]: !hideIcon })}
        onKeyDown={handleEscPress}
        data-cy={identifier}
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={handleReset}
          tabIndex={0}
          className={styles.hotFilterSearchClearIconWrapper}
          data-cy={`${identifier}-close-button`}
        >
          <Icon
            name="close-default"
            className={styles.hotFilterSearchClearIcon}
            ariaLabel={t('MODAL.BUTTON.CLOSE')}
          />
        </button>
      )}
    </div>
  );
});

HotFilterSearchInput.displayName = 'HotFilterSearchInput';

export default HotFilterSearchInput;
